/** Shared column-picker rules — show core columns only, not stacked sublines. */

export interface PickerColumn {
  key: string;
  label?: string;
  /** Short flat name for column picker rows (table headers keep `label`). */
  pickerLabel?: string;
  sublineOf?: string;
  pickerHidden?: boolean;
  compactHidden?: boolean;
  locked?: boolean;
}

export const PICKER_HIDDEN_COLUMN_KEYS = new Set([
  "_actions",
  "actions",
  "expand",
]);

export function isPickerColumn(
  column: PickerColumn,
  opts?: { compact?: boolean },
): boolean {
  /** Web expanded tables: sublines are separate columns and belong in the picker. Mobile/compact omit them. */
  if (column.sublineOf && opts?.compact !== false) return false;
  if (column.pickerHidden) return false;
  if (PICKER_HIDDEN_COLUMN_KEYS.has(column.key)) return false;
  if (!column.label?.trim()) return false;
  if (opts?.compact && column.compactHidden) return false;
  return true;
}

export function filterPickerColumns<T extends PickerColumn>(
  columns: T[],
  opts?: { compact?: boolean },
): T[] {
  return columns.filter((column) => isPickerColumn(column, opts));
}

/** Flat single-line label for picker rows — no stacked "Parent / child" suffixes. */
export function pickerColumnLabel(column: PickerColumn): string {
  const explicit = column.pickerLabel?.trim();
  if (explicit) return explicit;
  const label = column.label?.trim() ?? "";
  const slash = label.indexOf(" / ");
  return slash >= 0 ? label.slice(0, slash).trim() : label;
}

/** Broker / account short marks — leading, hideable, never locked. */
export const LEDGER_MARK_COLUMN_KEYS = ["broker_mark", "account_short"] as const;
/** Analytics(L) day tables use system/account keys for the same short marks. */
export const LEDGER_MARK_COLUMN_KEYS_ANALYTICS = ["system", "account"] as const;

const ALL_LEDGER_MARK_KEYS = new Set<string>([
  ...LEDGER_MARK_COLUMN_KEYS,
  ...LEDGER_MARK_COLUMN_KEYS_ANALYTICS,
]);

/** Ticker stays pinned by default so horizontal scroll still keeps the identity column. */
export function isDefaultPinnedColumnKey(key: string): boolean {
  return key === "ticker" || key === "underlying_ticker";
}

export function isLedgerMarkColumnKey(key: string): boolean {
  return ALL_LEDGER_MARK_KEYS.has(key);
}

function orderLedgerMarkColumns<T extends { key: string }>(columns: T[]): T[] {
  if (!columns.length) return columns;
  const hasShortMarks = columns.some(
    (column) => column.key === "broker_mark" || column.key === "account_short",
  );
  const markKeyList = hasShortMarks
    ? LEDGER_MARK_COLUMN_KEYS
    : LEDGER_MARK_COLUMN_KEYS_ANALYTICS;
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const ordered: T[] = [];
  for (const key of markKeyList) {
    const column = byKey.get(key);
    if (column) ordered.push(column);
  }
  for (const column of columns) {
    if (!ordered.includes(column)) ordered.push(column);
  }
  return ordered;
}

/**
 * Keep Br / Ac (or Analytics Broker / Account) at the far left.
 * Leaves expand chrome first when present. Marks stay unlockable.
 */
export function ensureLedgerMarkColumnsLeading<T extends { key: string }>(columns: T[]): T[] {
  if (!columns.length) return columns;
  const hasShortMarks = columns.some(
    (column) => column.key === "broker_mark" || column.key === "account_short",
  );
  const markKeyList = hasShortMarks
    ? LEDGER_MARK_COLUMN_KEYS
    : LEDGER_MARK_COLUMN_KEYS_ANALYTICS;
  const markKeys = new Set<string>(markKeyList);
  const marksInOrder: T[] = [];
  for (const key of markKeyList) {
    const column = columns.find((entry) => entry.key === key);
    if (column) marksInOrder.push(column);
  }
  if (!marksInOrder.length) return columns;
  const rest = columns.filter((column) => !markKeys.has(column.key));
  const keepLeading: T[] = [];
  while (rest.length && rest[0]?.key === "expand") {
    keepLeading.push(rest.shift()!);
  }
  return [...keepLeading, ...marksInOrder, ...rest];
}

/**
 * Keep expand chrome first when present. Pinning must not change column order —
 * sticky offsets follow the user's order among pinned columns.
 */
export function finalizePinnedColumnOrder<T extends { key: string; pinned?: boolean }>(
  columns: T[],
): T[] {
  if (!columns.length) return columns;
  const expand: T[] = [];
  const rest: T[] = [];
  for (const column of columns) {
    if (column.key === "expand") expand.push(column);
    else rest.push(column);
  }
  return expand.length ? [...expand, ...rest] : columns;
}

/** Toggle pin without reordering — pin is sticky only, not a move. */
export function toggleColumnPinned<T extends { key: string; pinned?: boolean }>(
  columns: T[],
  key: string,
  pinned: boolean,
): T[] {
  return columns.map((column) => (column.key === key ? { ...column, pinned } : column));
}

export function pinnedColumnKeys<T extends { key: string; pinned?: boolean; visible?: boolean }>(
  columns: T[],
): string[] {
  return columns
    .filter((column) => column.pinned && column.visible !== false)
    .map((column) => column.key);
}

/** Cumulative left offsets (px) for pinned columns from known widths. */
export function pinnedColumnLeftOffsets<
  T extends { key: string; pinned?: boolean; visible?: boolean; width?: number },
>(columns: T[], widthFor: (column: T) => number): Map<string, number> {
  const offsets = new Map<string, number>();
  let left = 0;
  for (const column of columns) {
    if (column.visible === false || !column.pinned) continue;
    offsets.set(column.key, left);
    left += Math.max(0, widthFor(column));
  }
  return offsets;
}

/** Saved order, with columns added later inserted beside their default neighbor. */
export function mergeSavedColumns<
  T extends PickerColumn & { visible?: boolean; pinned?: boolean },
>(
  defaults: T[],
  saved: Array<{ key?: string; visible?: boolean; pinned?: boolean }> | null | undefined,
  opts?: { alwaysVisibleKey?: string },
): T[] {
  if (!saved?.length) {
    return finalizePinnedColumnOrder(
      defaults.map((column) => ({
        ...column,
        pinned: column.pinned ?? isDefaultPinnedColumnKey(column.key),
      })),
    );
  }
  const byKey = new Map(saved.map((column) => [column.key, column]));
  const ordered = saved
    .map((entry) => defaults.find((column) => column.key === entry.key))
    .filter((column): column is T => Boolean(column))
    .map((column) => {
      const savedEntry = byKey.get(column.key);
      return {
        ...column,
        visible:
          column.key === opts?.alwaysVisibleKey || savedEntry?.visible !== false,
        pinned:
          typeof savedEntry?.pinned === "boolean"
            ? savedEntry.pinned
            : (column.pinned ?? isDefaultPinnedColumnKey(column.key)),
      };
    });
  const seen = new Set(ordered.map((column) => column.key));
  for (const [index, column] of defaults.entries()) {
    if (seen.has(column.key)) continue;
    const prevKey = index > 0 ? defaults[index - 1].key : null;
    const prevAt = prevKey ? ordered.findIndex((entry) => entry.key === prevKey) : -1;
    const insertAt = prevKey == null ? 0 : prevAt >= 0 ? prevAt + 1 : ordered.length;
    ordered.splice(insertAt, 0, {
      ...column,
      visible: column.visible !== false,
      pinned: column.pinned ?? isDefaultPinnedColumnKey(column.key),
    });
    seen.add(column.key);
  }
  return finalizePinnedColumnOrder(ordered);
}

export function rebuildPickerColumnOrder<T extends PickerColumn>(
  columns: T[],
  pickerOrder: string[],
): T[] {
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const used = new Set<string>();
  const ordered: T[] = [];

  const pickerKeys = new Set(pickerOrder);
  for (const key of pickerOrder) {
    const column = byKey.get(key);
    if (!column || used.has(key)) continue;
    ordered.push(column);
    used.add(key);
    for (const subline of columns.filter((entry) => entry.sublineOf === key)) {
      if (used.has(subline.key) || pickerKeys.has(subline.key)) continue;
      ordered.push(subline);
      used.add(subline.key);
    }
  }

  for (const column of columns) {
    if (!used.has(column.key)) ordered.push(column);
  }
  return ordered;
}

export function movePickerColumn<T extends PickerColumn>(
  columns: T[],
  pickerIndex: number,
  direction: "up" | "down",
  opts?: { compact?: boolean },
): T[] {
  const pickerCols = filterPickerColumns(columns, opts);
  const target = direction === "up" ? pickerIndex - 1 : pickerIndex + 1;
  if (target < 0 || target >= pickerCols.length) return columns;
  if (pickerCols[pickerIndex]?.locked || pickerCols[target]?.locked) return columns;

  const order = pickerCols.map((column) => column.key);
  const temp = order[pickerIndex]!;
  order[pickerIndex] = order[target]!;
  order[target] = temp;
  return rebuildPickerColumnOrder(columns, order);
}

export function togglePickerColumnVisibility<T extends PickerColumn>(
  columns: T[],
  key: string,
  visible: boolean,
  lockedKeys: ReadonlySet<string> = new Set(),
): T[] {
  return columns.map((column) => {
    if (lockedKeys.has(column.key)) return column;
    if (column.key === key) return { ...column, visible };
    if (column.sublineOf === key) return { ...column, visible };
    return column;
  });
}
